import React, { useState, useEffect, useRef, useMemo } from "react"
import "../../asset/styles/hotelDetail/hotelDetail.scss"
import InputText from "../../component/inputText/inputText"
import Select from "../../component/select/select"
import Checkbox from "../../component/checkbox/checkbox"
import Button from "../../component/button/button"
import Toggle from "../../component/toggle/Toggle"
import IconsColor from "../../util/iconsColor"
import Grid from "@mui/material/Grid"
import TextArea from "../../component/textArea/textArea"
import { useLocation, useParams } from "react-router-dom"
import { agent } from "../../apiClient/api"
import { useLoader } from "../../context/loaderContext"
import Loader from "../../component/loader/loaderComponent"
import { useSnackbar } from "../../context/snackbarContext"
import { HotelAmenityModel, HotelContractModel, HotelDetailModel } from "../../models"
import { useHotelGeneric } from "../../context/HotelGenericContext"
import { HotelDayWisePricingProvider } from "../../context/HotelDayWisePricingContext"
import HotelContract from "../../component/hotelContract/hotelContract"
import RoomCategories from "../../component/roomCategories/roomCategories"
import HotelRates from "../../component/hotelRates/hotelRates"
import { contractsWithDayWiseSurchargesCleared } from "../../component/hotelContract/dayWisePricingMaps"
import { IHotelContractValidationError } from "../../component/hotelContract/hotelContractTypes"
import { isContractWithoutSeason } from "../../component/hotelContract/hotelContractValidationUtil"
import HotelValidationErrorsList, { IContractValidationErrorGroup, IHotelValidationErrorItem } from "../../component/hotelDetail/hotelValidationErrorsList"
import RatesOverview from "../../component/hotelRates/RatesOverview"
import Popup from "../../component/popup/popup"
import PhoneInputComponent from "../../component/phoneInput/phoneInput"
import { useNavigate } from "react-router-dom"
import { toTitleCase } from "util/textcase"
import { normalizeWebsiteUrl, validateWebsiteUrl } from "util/helpers"

const HotelDetail: React.FC = () => {
    const { hotel_id } = useParams() // get the id from the url
    const navigate = useNavigate()
    const location = useLocation()
    const [activeTab, setActiveTab] = useState(() => {
        const requestedTab = (location.state as { tab?: string } | null)?.tab
        return requestedTab === "hotel-details" ? "hotel-details" : "rates-overview"
    })
    const { setIsLoading, isLoading } = useLoader()
    const { showError, showSuccess } = useSnackbar()
    const [hotelDetailData, setHotelDetailData] = useState<HotelDetailModel>()
    const { hotelGenericData, setHotelGenericData } = useHotelGeneric()
    const [contractErrors, setContractErrors] = useState<Map<number, IHotelContractValidationError[]>>(new Map())
    const [selectedRatesContractId, setSelectedRatesContractId] = useState<string | null>(null)
    const [showHotelDetailsValidationErrors, setShowHotelDetailsValidationErrors] = useState(false)
    const [showRoomCategoriesValidationErrors, setShowRoomCategoriesValidationErrors] = useState(false)
    const [showHotelRatesValidationErrors, setShowHotelRatesValidationErrors] = useState(false)
    const [latitudeError, setLatitudeError] = useState<string | undefined>(undefined)
    const [longitudeError, setLongitudeError] = useState<string | undefined>(undefined)
    const [addressError, setAddressError] = useState<string | undefined>(undefined)
    const [contactNumberError, setContactNumberError] = useState<string | undefined>(undefined)
    const [hotelLinkError, setHotelLinkError] = useState<string | undefined>(undefined)
    const [ratingError, setRatingError] = useState<string | undefined>(undefined)

    // Hotel name is edited inline: locked by default, unlocked by the pencil icon and
    // saved only when the tick icon is clicked. The draft is kept out of hotelDetailData
    // so an unconfirmed edit never counts as an unsaved change.
    const [isHotelNameEditing, setIsHotelNameEditing] = useState(false)
    const [isHotelNameSaving, setIsHotelNameSaving] = useState(false)
    const [hotelNameDraft, setHotelNameDraft] = useState("")
    const [hotelNameError, setHotelNameError] = useState<string | undefined>(undefined)

    // Track original data for unsaved changes detection
    const originalHotelDetailData = useRef<HotelDetailModel | undefined>(undefined)
    const isInitialLoad = useRef<boolean>(true)

    // Refs to access child component save functions
    const roomCategoriesSaveRef = useRef<(() => Promise<boolean>) | null>(null)
    const hotelRatesSaveRef = useRef<(() => Promise<boolean>) | null>(null)

    // Track errors from child components
    const [roomCategoriesHasErrors, setRoomCategoriesHasErrors] = useState(false)
    const [hotelRatesHasErrors, setHotelRatesHasErrors] = useState(false)

    // Popup states
    const [showErrorPopup, setShowErrorPopup] = useState(false)
    const [showValidationErrorsPopup, setShowValidationErrorsPopup] = useState(false)
    const [showUnsavedChangesPopup, setShowUnsavedChangesPopup] = useState(false)
    const [showDeleteContractPopup, setShowDeleteContractPopup] = useState(false)
    const [contractToDelete, setContractToDelete] = useState<{ contract: HotelContractModel; index: number } | null>(null)
    const [pendingTab, setPendingTab] = useState<string | null>(null)

    // Contract validation errors grouped per contract; stale entries of deleted contracts are ignored
    const contractErrorGroups = useMemo((): IContractValidationErrorGroup[] => {
        const contractCount = Math.max(hotelDetailData?.contract?.length ?? 0, 1)
        return Array.from(contractErrors.entries())
            .filter(([contractIndex, errors]) => contractIndex < contractCount && errors.length > 0)
            .sort(([a], [b]) => a - b)
            .map(([contractIndex, errors]) => ({
                contractIndex,
                contract: hotelDetailData?.contract?.[contractIndex],
                errors,
            }))
    }, [contractErrors, hotelDetailData?.contract])

    const handleContractErrorsChange = (contractIndex: number, errors: IHotelContractValidationError[]) => {
        setContractErrors((prev) => {
            const newErrors = new Map(prev)
            newErrors.set(contractIndex, errors)
            return newErrors
        })
    }

    const GetHotelDetails = async (skipLoader?: boolean): Promise<HotelDetailModel | undefined> => {
        try {
            if (!skipLoader) {
                setIsLoading(true)
            }
            const response = await agent.HotelDetailAPI.get(String(hotel_id), skipLoader)
            if (response?.status === false) {
                showError(response?.error?.message)
                return undefined
            }
            const freshData = response?.data[0]
            setHotelDetailData(freshData)
            // Update original data if this is not the initial load (i.e., after a save)
            if (!isInitialLoad.current) {
                // After a save, immediately update the original data reference
                originalHotelDetailData.current = JSON.parse(JSON.stringify(freshData))
            }
            // Note: isInitialLoad.current is managed by the useEffect below
            return freshData
        } catch (error) {
            showError("Failed to get hotel details")
            return undefined
        } finally {
            if (!skipLoader) {
                setIsLoading(false)
            }
        }
    }

    useEffect(() => {
        GetHotelDetails()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hotel_id])

    // Store original data after component has fully mounted and all child effects have run
    // This only runs once after the initial load
    useEffect(() => {
        if (!hotelDetailData || !isInitialLoad.current || isLoading) return

        // Use setTimeout to ensure all child component effects and state updates have completed
        const timer = setTimeout(() => {
            // Only set original data if this is truly the initial load
            if (isInitialLoad.current) {
                originalHotelDetailData.current = JSON.parse(JSON.stringify(hotelDetailData))
                isInitialLoad.current = false
            }
        }, 1000)

        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hotelDetailData, isLoading])

    const GetGenericHotelDetails = async () => {
        try {
            setIsLoading(true)
            const response = await agent.GenericGlobalAPI.get()
            if (response?.status === false) {
                showError(response?.error || "Failed to fetch generic hotel details")
                setIsLoading(false)
            } else {
                setHotelGenericData(response?.data[0])
                setIsLoading(false)
            }
        } catch (error) {
            showError("Failed to fetch generic hotel details")
            setIsLoading(false)
        }
    }

    useEffect(() => {
        GetGenericHotelDetails()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Initialize selected rates contract when data loads
    useEffect(() => {
        if (hotelDetailData?.contract && hotelDetailData.contract.length > 0 && !selectedRatesContractId) {
            setSelectedRatesContractId(hotelDetailData.contract[0].contract_id || null)
        }
    }, [hotelDetailData, selectedRatesContractId])

    const ratesDayWisePricingScopeId = useMemo(() => {
        const list = hotelDetailData?.contract ?? []
        if (!list.length) return null as string | null
        const c = selectedRatesContractId
            ? list.find((x) => x.contract_id === selectedRatesContractId)
            : list[0]
        if (!c) return null
        const idx = list.indexOf(c)
        return String(c.contract_id ?? `local-${idx}`)
    }, [hotelDetailData?.contract, selectedRatesContractId])

    // Common function to handle changes in hotelDetailData
    const handleHotelDetailChange = (field: keyof HotelDetailModel, value: any) => {
        setHotelDetailData((prevData) => ({
            ...prevData!,
            [field]: value,
        }))
    }

    // Validate latitude (-90 to 90)
    const validateLatitude = (value: string): string | undefined => {
        if (!value || value.trim() === '') {
            return undefined // Empty is allowed
        }

        const num = parseFloat(value)

        if (isNaN(num)) {
            return 'Latitude must be a valid number'
        }

        if (num < -90 || num > 90) {
            return 'Latitude must be between -90 and 90'
        }

        return undefined
    }

    // Validate longitude (-180 to 180)
    const validateLongitude = (value: string): string | undefined => {
        if (!value || value.trim() === '') {
            return undefined // Empty is allowed
        }

        const num = parseFloat(value)

        if (isNaN(num)) {
            return 'Longitude must be a valid number'
        }

        if (num < -180 || num > 180) {
            return 'Longitude must be between -180 and 180'
        }

        return undefined
    }

    // Handle latitude change with validation
    const handleLatitudeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        handleHotelDetailChange('latitude', value)

        const error = validateLatitude(value)
        setLatitudeError(error)
    }

    // Handle latitude blur to update via API
    const handleLatitudeBlur = () => {
        if (latitudeError) return

        // For non-required field: send null if empty
        const currentLatitude = hotelDetailData?.latitude ? parseFloat(hotelDetailData.latitude) : null
        const originalLatitude = originalHotelDetailData.current?.latitude ? parseFloat(originalHotelDetailData.current.latitude) : null

        // Only update if value has changed
        if (currentLatitude !== originalLatitude) {
            updateHotelDetailField({ latitude: currentLatitude != null ? currentLatitude.toString() : null })
        }
    }

    // Handle longitude change with validation
    const handleLongitudeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        handleHotelDetailChange('longitude', value)

        const error = validateLongitude(value)
        setLongitudeError(error)
    }

    // Handle longitude blur to update via API
    const handleLongitudeBlur = () => {
        if (longitudeError) return

        // For non-required field: send null if empty
        const currentLongitude = hotelDetailData?.longitude ? parseFloat(hotelDetailData.longitude) : null
        const originalLongitude = originalHotelDetailData.current?.longitude ? parseFloat(originalHotelDetailData.current.longitude) : null

        // Only update if value has changed
        if (currentLongitude !== originalLongitude) {
            updateHotelDetailField({ longitude: currentLongitude != null ? currentLongitude.toString() : null })
        }
    }

    // Function to update individual hotel detail fields via API
    const updateHotelDetailField = async (fieldUpdates: Partial<HotelDetailModel>): Promise<boolean> => {
        if (!hotelDetailData?.hotel_id) return false

        try {
            const payload: any = {
                hotel_id: hotelDetailData.hotel_id,
                ...fieldUpdates
            }

            // Send contact_number as a string with the "+" country code; strip spaces only
            if (payload.contact_number !== undefined) {
                payload.contact_number = payload.contact_number.replace(/\s/g, '')
            }

            const response = await agent.UpdateHotelDetailsAPI.put(payload)
            if (response?.status === false) {
                showError(response?.error?.message || "Failed to update hotel details")
                return false
            } else {
                showSuccess("Hotel details updated successfully")
                // Update only the specific fields that were saved in originalHotelDetailData
                // This preserves unsaved changes in other fields (like contracts)
                if (originalHotelDetailData.current) {
                    Object.keys(fieldUpdates).forEach((key) => {
                        const typedKey = key as keyof HotelDetailModel
                        if (fieldUpdates[typedKey] !== undefined) {
                            ; (originalHotelDetailData.current as any)[typedKey] = JSON.parse(
                                JSON.stringify(fieldUpdates[typedKey])
                            )
                        }
                    })
                }
                return true
            }
        } catch (error) {
            showError("Failed to update hotel details")
            return false
        }
    }

    const HOTEL_NAME_INPUT_ID = "hotel-name-input"

    const handleHotelNameEditClick = () => {
        setHotelNameDraft(toTitleCase(hotelDetailData?.hotel_name) || "")
        setHotelNameError(undefined)
        setIsHotelNameEditing(true)
        // Focus after the input has been re-rendered as enabled
        setTimeout(() => {
            const input = document.getElementById(HOTEL_NAME_INPUT_ID) as HTMLInputElement | null
            input?.focus()
            input?.select()
        }, 0)
    }

    const handleHotelNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setHotelNameDraft(e.target.value)
        if (hotelNameError) {
            setHotelNameError(undefined)
        }
    }

    const handleHotelNameConfirmClick = async () => {
        if (isHotelNameSaving) return

        const trimmedHotelName = hotelNameDraft.trim()
        if (trimmedHotelName.length === 0) {
            setHotelNameError("Hotel name is required")
            return
        }

        const newHotelName = toTitleCase(trimmedHotelName)
        const savedHotelName = toTitleCase(hotelDetailData?.hotel_name) || ""

        // Nothing changed: just lock the field again without calling the API
        if (newHotelName === savedHotelName) {
            setHotelNameError(undefined)
            setIsHotelNameEditing(false)
            return
        }

        setIsHotelNameSaving(true)
        const isUpdated = await updateHotelDetailField({ hotel_name: newHotelName })
        setIsHotelNameSaving(false)

        // On failure (e.g. duplicate name in the city) the draft is discarded and the
        // previously saved name stays visible; the error is surfaced by the API handler.
        if (isUpdated) {
            handleHotelDetailChange("hotel_name", newHotelName)
        }
        setHotelNameError(undefined)
        setIsHotelNameEditing(false)
    }

    // Handler for address blur
    const handleAddressBlur = () => {
        // For required field: show error if empty, don't call API
        if (!hotelDetailData?.address || hotelDetailData.address.trim().length === 0) {
            setAddressError("Address is required")
            return
        }

        // Clear error if field has value
        setAddressError(undefined)

        // Only update if value has changed
        if (originalHotelDetailData.current?.address !== hotelDetailData.address) {
            updateHotelDetailField({ address: hotelDetailData.address.trim() })
        }
    }

    // Handler for contact number blur
    const handleContactNumberBlur = () => {
        // For required field: show error if empty, don't call API
        if (!hotelDetailData?.contact_number || hotelDetailData.contact_number.replace(/\s/g, '').trim().length === 0) {
            setContactNumberError("Contact number is required")
            return
        }

        if (!hotelDetailData?.contact_number || hotelDetailData.contact_number.replace(/\s/g, '').trim().length !== 12) {
            setContactNumberError("Contact number must be 10 digits")
            return
        }

        // Clear error if field has value
        setContactNumberError(undefined)

        // Only update if value has changed
        if (originalHotelDetailData.current?.contact_number !== hotelDetailData.contact_number) {
            updateHotelDetailField({ contact_number: hotelDetailData.contact_number })
        }
    }

    // Handler for google place ID blur
    const handleGooglePlaceIdBlur = () => {
        // For non-required field: send null if empty
        if (originalHotelDetailData.current?.google_place_id !== hotelDetailData?.google_place_id) {
            updateHotelDetailField({ google_place_id: hotelDetailData?.google_place_id || null })
        }
    }

    const handleHotelLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        handleHotelDetailChange("hotel_link", value)
        setHotelLinkError(validateWebsiteUrl(value))
    }

    const handleHotelLinkBlur = () => {
        if (hotelLinkError) return

        const normalizedHotelLink = normalizeWebsiteUrl(hotelDetailData?.hotel_link)
        const originalHotelLink = normalizeWebsiteUrl(originalHotelDetailData.current?.hotel_link)

        if (normalizedHotelLink !== originalHotelLink) {
            if (normalizedHotelLink !== hotelDetailData?.hotel_link) {
                handleHotelDetailChange("hotel_link", normalizedHotelLink)
            }
            updateHotelDetailField({ hotel_link: normalizedHotelLink })
        }
    }


    // Helper function to check if contract dates overlap
    const contractDatesOverlap = (start1: string | null, end1: string | null, start2: string | null, end2: string | null): boolean => {
        if (!start1 || !end1 || !start2 || !end2) return false
        const s1 = new Date(start1)
        const e1 = new Date(end1)
        const s2 = new Date(start2)
        const e2 = new Date(end2)
        return s1 <= e2 && s2 <= e1
    }

    // Validate contract dates don't overlap with other contracts
    const getContractDateErrors = (contractIndex: number, startDate: string | null, endDate: string | null): string[] => {
        try {
            const errors: string[] = []

            if (!startDate || !endDate) return errors

            // Check if start date is before end date
            if (new Date(startDate) > new Date(endDate)) {
                errors.push("Contract start date must be before end date")
            }

            // Check for overlaps with other contracts
            if (hotelDetailData?.contract) {
                hotelDetailData.contract.forEach((contract, index) => {
                    if (index !== contractIndex && contract.contract_start_date && contract.contract_end_date) {
                        const contractStart = typeof contract.contract_start_date === 'number'
                            ? new Date(contract.contract_start_date).toISOString().split('T')[0]
                            : contract.contract_start_date
                        const contractEnd = typeof contract.contract_end_date === 'number'
                            ? new Date(contract.contract_end_date).toISOString().split('T')[0]
                            : contract.contract_end_date

                        if (contractDatesOverlap(startDate, endDate, contractStart, contractEnd)) {
                            errors.push(`Contract dates overlap with another contract (${contractStart} to ${contractEnd})`)
                        }
                    }
                })
            }

            return errors
        } catch (error) {
            console.error("Error validating contract dates:", error)
            return []
        }
    }

    // Function to handle contract changes
    const handleContractChange = (contractIndex: number, updatedContract: HotelContractModel) => {
        setHotelDetailData((prevData) => {
            if (!prevData) return prevData

            const updatedContracts = [...prevData.contract]
            updatedContracts[contractIndex] = updatedContract

            return {
                ...prevData,
                contract: updatedContracts,
            }
        })
    }

    // Function to handle rates pricing changes
    const handleRatesPricingChange = (updatedPricing: any[]) => {
        if (!selectedRatesContractId) return

        setHotelDetailData((prevData) => {
            if (!prevData) return prevData

            const updatedContracts = prevData.contract.map((contract) => {
                if (contract.contract_id === selectedRatesContractId) {
                    return {
                        ...contract,
                        hotel_room_pricing: updatedPricing,
                    }
                }
                return contract
            })

            return {
                ...prevData,
                contract: updatedContracts,
            }
        })
    }

    // Function to handle all rates changes (pricing + extra bed + surcharges)
    const handleRatesFullChange = (contractId: string, updates: Partial<HotelContractModel>) => {
        setHotelDetailData((prevData) => {
            if (!prevData) return prevData

            const updatedContracts = prevData.contract.map((contract) => {
                if (contract.contract_id === contractId) {
                    return {
                        ...contract,
                        ...updates,
                    }
                }
                return contract
            })

            return {
                ...prevData,
                contract: updatedContracts,
            }
        })
    }

    // Function to handle hotel amenities changes
    const handleAmenityChange = (amenityId: number, checked: boolean) => {
        setHotelDetailData((prevData) => {
            if (!prevData) return prevData

            // Check if the amenity value has actually changed
            const originalAmenity = originalHotelDetailData.current?.hotel_amenities.find(
                (a: HotelAmenityModel) => a.hotel_amenities_type_id === amenityId
            )

            // Only update if value has changed
            if (originalAmenity?.flag_configured !== checked) {
                const updatedAmenities = prevData.hotel_amenities.map((x: HotelAmenityModel) => {
                    if (x.hotel_amenities_type_id === amenityId) {
                        return { ...x, flag_configured: checked }
                    }
                    return x
                })

                // Update via API
                updateHotelDetailField({
                    hotel_amenities: updatedAmenities.map(a => ({
                        hotel_amenities_type_id: a.hotel_amenities_type_id,
                        flag_configured: a.flag_configured,
                        hotel_amenities_type_name: a.hotel_amenities_type_name
                    }))
                })

                return { ...prevData, hotel_amenities: updatedAmenities }
            }

            return prevData
        })
    }

    // Function to handle hotel type change
    const handleHotelTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedTypeId = Number(e.target.value)

        // Only update if value has changed
        if (originalHotelDetailData.current?.hotel_type_id !== selectedTypeId) {
            const selectedType = hotelGenericData?.hotel_type?.find((x: any) => x.hotel_type_id === selectedTypeId)
            setHotelDetailData((prevData) => ({
                ...prevData!,
                hotel_type_id: selectedTypeId,
                hotel_type_name: selectedType?.hotel_type_name || "",
            }))
            updateHotelDetailField({ hotel_type_id: selectedTypeId })
        }
    }

    // Function to add a new contract
    const handleAddContract = () => {
        if (!hotelDetailData?.hotel_id) {
            showError("Hotel ID is required to add a contract")
            return
        }

        // Initialize with default rate structure type (first available)
        const defaultRateStructureTypeId = 1

        const newContract: HotelContractModel = {
            contract_id: null,
            hotel_id: hotelDetailData.hotel_id,
            contract_start_date: null,
            contract_end_date: null,
            flag_day_wise_pricing: false,
            flag_seasonal_extra_bed_pricing: false,
            flag_enable_peak_date_surcharges: false,
            flag_peak_surcharge_vary_by_room_type: false,
            flag_enable_per_person_seasonal_surcharge: false,
            free_child_age_limit: 4,
            chargeable_child_age_limit: 11,
            extra_bed_price: null,
            child_without_bed_price: null,
            child_with_bed: null,
            adult_with_bed: null,
            surcharge_per_additional_pax: null,
            rate_structure_type_id: defaultRateStructureTypeId,
            // Initialize with one default season to prevent the HotelContract component from adding one on mount
            season: [{
                season_id: `season_${Date.now()}`,
                season_name: "",
                flag_remaining_season: false,
                flag_compulsory_meal: false,
                season_date_range: [],
            }],
            seasonal_extra_bed_pricing: [],
            day_wise_pricing: [],
            hotel_room_pricing: [],
            meal_plan_type: [],
            // peak_date_surcharge_group: [],
            git_fit_applied_on_minimum_room: null,
            git_fit_applied_on_minimum_pax: null,
        }

        setHotelDetailData((prevData) => {
            if (!prevData) return prevData
            return {
                ...prevData,
                contract: [...prevData.contract, newContract],
            }
        })
    }

    // Open delete contract confirmation popup
    const handleDeleteContractClick = (contractItem: HotelContractModel, index: number) => {
        const contracts = hotelDetailData?.contract ?? []
        if (contracts.length <= 1) {
            showError("At least one contract is required. You cannot delete the only contract.")
            return
        }
        setContractToDelete({ contract: contractItem, index })
        setShowDeleteContractPopup(true)
    }

    // Perform contract delete after user confirms (from popup)
    const handleConfirmDeleteContract = async () => {
        if (!contractToDelete) return
        const { contract: contractItem, index } = contractToDelete
        setShowDeleteContractPopup(false)
        setContractToDelete(null)

        const contractId = contractItem.contract_id
        if (contractId) {
            try {
                setIsLoading(true)
                const response = await agent.DeleteContractAPI.delete(contractId, true)
                if (response?.status === true) {
                    showSuccess("Contract deleted successfully")
                    setContractErrors(new Map())
                    await GetHotelDetails(true)
                } else {
                    showError(response?.error?.message || "Failed to delete contract")
                }
            } catch {
                showError("Failed to delete contract")
            } finally {
                setIsLoading(false)
            }
        } else {
            // Unsaved contract: remove from local state only
            setHotelDetailData((prevData) => {
                if (!prevData || !prevData.contract) return prevData
                const nextContract = prevData.contract.filter((_, i) => i !== index)
                return { ...prevData, contract: nextContract }
            })
            setContractErrors((prev) => {
                const next = new Map<number, IHotelContractValidationError[]>()
                prev.forEach((errors, key) => {
                    if (key < index) next.set(key, errors)
                    if (key > index) next.set(key - 1, errors)
                })
                return next
            })
            showSuccess("Contract removed")
        }
    }

    const handleCancelDeleteContract = () => {
        setShowDeleteContractPopup(false)
        setContractToDelete(null)
    }

    // Check if current tab has validation errors
    const hasCurrentTabErrors = (): boolean => {
        switch (activeTab) {
            case "rates-overview":
                return false

            case "hotel-details":
                // Check for hotel details validation errors
                if (!hotelDetailData) return false

                const hasValidHotelType = hotelDetailData.hotel_type_id !== null && hotelDetailData.hotel_type_id !== undefined
                const hasValidContactNumber = !!(hotelDetailData.contact_number && hotelDetailData.contact_number.replace(/\s/g, '').trim().length > 0)
                const hasValidAddress = !!(hotelDetailData.address && hotelDetailData.address.trim().length > 0)
                const hasValidRating = !!(hotelDetailData.rating && hotelDetailData.rating > 0)
                const hasValidHotelLink = !hotelLinkError

                // Check contracts
                const contractsValid = (hotelDetailData.contract ?? []).every((contract: HotelContractModel) => {
                    const startDate = typeof contract.contract_start_date === 'string'
                        ? contract.contract_start_date
                        : contract.contract_start_date
                    const endDate = typeof contract.contract_end_date === 'string'
                        ? contract.contract_end_date
                        : contract.contract_end_date

                    const hasStartDate = !!(startDate && (typeof startDate === 'string' ? startDate.trim().length > 0 : true))
                    const hasEndDate = !!(endDate && (typeof endDate === 'string' ? endDate.trim().length > 0 : true))
                    return hasStartDate && hasEndDate
                })

                // Check if any contract has date errors
                const hasContractDateErrors = contractErrorGroups.length > 0

                const hasErrors = !hasValidHotelType || !hasValidContactNumber || !hasValidAddress || !hasValidRating || !hasValidHotelLink || !contractsValid || hasContractDateErrors

                return hasErrors

            case "room-categories":
                return roomCategoriesHasErrors

            case "rates":
                return hotelRatesHasErrors

            default:
                return false
        }
    }

    // Normalize data for comparison by removing temporary IDs and sorting arrays
    const normalizeForComparison = (data: any, parentKey?: string): any => {
        if (data === null || data === undefined) {
            return data
        }

        if (Array.isArray(data)) {
            // Filter out items that are "remaining" date ranges (auto-generated)
            const filteredData = data.filter(item => {
                if (item && typeof item === 'object') {
                    // Check values for "remaining_" prefix
                    return !Object.values(item).some(val =>
                        typeof val === 'string' && val.startsWith('remaining_')
                    )
                }
                return true
            })

            // Normalize each item first, then sort
            const normalizedArray = filteredData.map(item => normalizeForComparison(item, parentKey))

            // Sort arrays by a stable key if available
            return normalizedArray.sort((a, b) => {
                // Try multiple keys for sorting
                const aKey = a?.hotel_room_type_id || a?.contract_id || a?.season_id ||
                    a?.season_date_range_id || a?.range_id ||
                    a?.hotel_amenities_type_id || a?.day_wise_pricing_group_id ||
                    JSON.stringify(a)
                const bKey = b?.hotel_room_type_id || b?.contract_id || b?.season_id ||
                    b?.season_date_range_id || b?.range_id ||
                    b?.hotel_amenities_type_id || b?.day_wise_pricing_group_id ||
                    JSON.stringify(b)

                // Handle null keys
                if (aKey === null && bKey === null) return 0
                if (aKey === null) return 1
                if (bKey === null) return -1

                return String(aKey).localeCompare(String(bKey))
            })
        }

        if (typeof data === 'object') {
            const normalized: any = {}

            // Sort object keys for consistent ordering
            const sortedKeys = Object.keys(data).sort()

            for (const key of sortedKeys) {
                const value = data[key]

                // Skip fields that are auto-generated or temporary
                if (key === 'pricing_group_day_id' || key === 'hotel_room_pricing_id' || key === 'room_type_day_group_pricing_id' || key === 'hotel_room_day_group_pricing_id') {
                    // These IDs are always null or auto-generated, skip them
                    continue
                }

                // Ignore season_date_range when flag_remaining_season is true
                if (key === 'season_date_range' && data.flag_remaining_season === true) {
                    // Skip season_date_range comparison when flag_remaining_season is true
                    continue
                }

                if (key === 'meal_plan_type' && data.meal_plan_type?.length === 0) {
                    // Skip season_date_range comparison when flag_remaining_season is true
                    continue
                }

                if (key === 'hotel_room_day_group_pricing' && data.hotel_room_day_group_pricing?.length === 0) {
                    // Skip hotel_room_day_group_pricing comparison when season is empty
                    continue
                }

                if (key === 'meal_plan' && data.meal_plan?.length === 0) {
                    // Skip hotel_room_day_group_pricing comparison when season is empty
                    continue
                }

                if (key === 'per_person_seasonal_surcharge' && data.per_person_seasonal_surcharge?.length === 0) {
                    // Skip hotel_room_day_group_pricing comparison when season is empty
                    continue
                }

                if (key === 'room_specific_different_surcharge' && data.room_specific_different_surcharge?.length === 0) {
                    // Skip hotel_room_day_group_pricing comparison when season is empty
                    continue
                }

                if (key === 'surcharge_per_additional_pax') {
                    continue
                }

                if (key === 'git_fit_applied_on_minimum_pax') {
                    continue
                }

                if (key === 'sequence' && data.sequence != null) {
                    data.sequence = parseInt(data.sequence)
                }

                if (key === 'adult_with_bed') {
                    continue
                }
                // Normalize numeric fields: treat 0 and null as equivalent for price fields
                // Note: child_without_bed_price allows 0 as valid value, so exclude it from normalization
                else if ((key === 'extra_bed_price' ||
                    key === 'surcharge_amount' || key === 'charges' || key === 'price') &&
                    (value === 0 || value === null)) {
                    // Normalize to null for consistent comparison
                    normalized[key] = null
                } else {
                    // Recursively normalize all other values
                    normalized[key] = normalizeForComparison(value, key)
                }
            }
            return normalized
        }

        return data
    }

    // Check if current tab has unsaved changes
    const hasUnsavedChanges = (): boolean => {
        // Don't check for changes during initial load
        if (isInitialLoad.current) {
            return false
        }

        if (!originalHotelDetailData.current || !hotelDetailData) {
            return false
        }

        // Normalize both objects before comparison
        const normalizedOriginal = normalizeForComparison(originalHotelDetailData.current)
        const normalizedCurrent = normalizeForComparison(hotelDetailData)
        // Deep comparison using JSON stringify on normalized data
        const original = JSON.stringify(normalizedOriginal)
        const current = JSON.stringify(normalizedCurrent)

        const hasChanges = original !== current
        return hasChanges
    }

    // Validate hotel details
    // Errors of the hotel's own fields, listed in the validation errors popup.
    // Contract field errors (including empty contract dates) come from each HotelContract via contractErrorGroups.
    const getHotelFieldErrors = (): IHotelValidationErrorItem[] => {
        const errors: IHotelValidationErrorItem[] = []
        if (!hotelDetailData) return errors

        if (hotelDetailData.hotel_type_id === null || hotelDetailData.hotel_type_id === undefined) {
            errors.push({ field: "Hotel Type", message: "Hotel type is required." })
        }
        if (!(hotelDetailData.contact_number && hotelDetailData.contact_number.replace(/\s/g, '').trim().length > 0)) {
            errors.push({ field: "Contact Number", message: "Contact number is required." })
        }
        const hotelLinkValidationError = validateWebsiteUrl(hotelDetailData.hotel_link || "")
        if (hotelLinkValidationError) {
            errors.push({ field: "Website", message: hotelLinkValidationError })
        }
        if (!(hotelDetailData.address && hotelDetailData.address.trim().length > 0)) {
            errors.push({ field: "Address", message: "Address is required." })
        }
        if (!(hotelDetailData.rating && hotelDetailData.rating > 0)) {
            errors.push({ field: "Star Rating", message: "Star rating is required." })
        }
        return errors
    }

    // Handle tab change with validation
    const handleTabChange = (newTab: string) => {
        // If clicking on the same tab, do nothing
        if (newTab === activeTab) return

        // Step 1: Check for errors
        if (hasCurrentTabErrors()) {
            setPendingTab(newTab)
            setShowErrorPopup(true)
            return
        }

        // Step 2: Check for unsaved changes
        if (hasUnsavedChanges()) {
            setPendingTab(newTab)
            setShowUnsavedChangesPopup(true)
            return
        }

        // Step 3: No errors and no changes, navigate directly
        // Reset validation error states when successfully switching tabs
        setShowHotelDetailsValidationErrors(false)
        setShowRoomCategoriesValidationErrors(false)
        setShowHotelRatesValidationErrors(false)

        setActiveTab(newTab)
    }

    // Handle Save and Continue action based on current tab
    const handleSaveAndContinue = async () => {
        let saveSuccess = false

        try {
            // Call the appropriate save function based on the active tab
            switch (activeTab) {
                case "hotel-details":
                    saveSuccess = await handleSaveHotelDetails()
                    break

                case "room-categories":
                    if (roomCategoriesSaveRef.current) {
                        saveSuccess = await roomCategoriesSaveRef.current()
                        if (saveSuccess) {
                            // Refresh hotel details to get updated data
                            // GetHotelDetails will update originalHotelDetailData.current automatically
                            // await GetHotelDetails(true)

                            console.log("Room Categories saved successfully")
                        }
                    }
                    break

                case "rates":
                    if (hotelRatesSaveRef.current) {
                        saveSuccess = await hotelRatesSaveRef.current()
                        if (saveSuccess) {
                            // Refresh hotel details to get updated data
                            // GetHotelDetails will update originalHotelDetailData.current automatically
                            // await GetHotelDetails(true)
                            console.log("Rates saved successfully")
                        }
                    }
                    break

                default:
                    saveSuccess = false
            }

            if (saveSuccess && pendingTab) {
                setShowUnsavedChangesPopup(false)

                // Reset validation error states after successful save
                setShowHotelDetailsValidationErrors(false)
                setShowRoomCategoriesValidationErrors(false)
                setShowHotelRatesValidationErrors(false)

                setActiveTab(pendingTab)
                setPendingTab(null)
            }
        } catch (error) {
            console.error("Error saving data:", error)
        }
    }

    // Handle Discard Changes action
    const handleDiscardChanges = () => {
        if (originalHotelDetailData.current) {
            setHotelDetailData(JSON.parse(JSON.stringify(originalHotelDetailData.current)))
        }
        setShowUnsavedChangesPopup(false)
        setShowErrorPopup(false)

        // Reset validation error states
        setShowHotelDetailsValidationErrors(false)
        setShowRoomCategoriesValidationErrors(false)
        setShowHotelRatesValidationErrors(false)
        setRatingError(undefined)
        setHotelLinkError(undefined)

        if (pendingTab) {
            setActiveTab(pendingTab)
            setPendingTab(null)
        }
    }

    // Handle Cancel action
    const handleCancelTabChange = () => {
        setShowUnsavedChangesPopup(false)
        setShowErrorPopup(false)
        setPendingTab(null)

        // Don't reset validation error states here - user should fix errors or discard changes
    }

    // Handle Fix Errors action - show all errors and scroll to first error
    const handleFixErrors = () => {
        // Close the popup first
        setShowErrorPopup(false)
        setShowValidationErrorsPopup(false)
        setPendingTab(null)

        // Show validation errors based on active tab
        if (activeTab === "hotel-details") {
            setShowHotelDetailsValidationErrors(true)
        } else if (activeTab === "room-categories") {
            setShowRoomCategoriesValidationErrors(true)
        } else if (activeTab === "rates") {
            setShowHotelRatesValidationErrors(true)
        }

        // Use setTimeout to ensure the errors are rendered before scrolling
        setTimeout(() => {
            // Find the first error field and scroll to it
            const errorSelectors = [
                'select[aria-invalid="true"]', // For Select components with errors
                'input[aria-invalid="true"]',  // For InputText components with errors
                'textarea[aria-invalid="true"]', // For TextArea components with errors
                '.error-message', // For any element with error-message class
                '[data-error="true"]' // For any element marked with error
            ]

            for (const selector of errorSelectors) {
                const errorElement = document.querySelector(selector)
                if (errorElement) {
                    errorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    })
                    // Try to focus the element if it's focusable
                    if (errorElement instanceof HTMLElement && (errorElement.tagName === 'INPUT' || errorElement.tagName === 'SELECT' || errorElement.tagName === 'TEXTAREA')) {
                        errorElement.focus()
                    }
                    break
                }
            }
        }, 100)
    }

    const handleSaveHotelDetails = async (): Promise<boolean> => {
        try {
            // Inline errors for rating and hotel link (optional, but must be valid when provided)
            setRatingError(!hotelDetailData?.rating || hotelDetailData.rating <= 0 ? "Star rating is required" : undefined)
            setHotelLinkError(validateWebsiteUrl(hotelDetailData?.hotel_link || "") || undefined)

            // All hotel field and contract errors are listed in one popup instead of a toast
            if (getHotelFieldErrors().length > 0 || contractErrorGroups.length > 0) {
                setShowHotelDetailsValidationErrors(true)
                // "Save and Continue" may have triggered this save — close that popup so the list stays on top
                setShowUnsavedChangesPopup(false)
                setPendingTab(null)
                setShowValidationErrorsPopup(true)
                return false
            }

            setShowHotelDetailsValidationErrors(false)
            setIsLoading(true)

            const normalizedHotelLink = normalizeWebsiteUrl(hotelDetailData?.hotel_link)

            // Build payload immutably; normalize season_id (null for temp ids like "season_*")
            const updatedContractData = {
                ...hotelDetailData,
                hotel_link: normalizedHotelLink,
                contract: hotelDetailData?.contract?.map((c: HotelContractModel) => ({
                    ...c,
                    // A single empty season row means the contract uses day groups only
                    season: isContractWithoutSeason(c.season ?? [])
                        ? []
                        : c.season?.map((s: any) => ({
                            ...s,
                            season_id: typeof s.season_id === "string" && s.season_id.includes("season_") ? null : s.season_id,
                            season_date_range: s.season_date_range ?? [],
                        })),
                })),
            }

            // Save hotel details
            const response = await agent.UpdateHotelAPI.put(updatedContractData as HotelDetailModel)
            if (response?.status === false) {
                showError(response?.error?.message)
                setIsLoading(false)
                return false
            }

            // Update hotel detail data with the response
            const updatedHotelData = response?.data[0]

            setHotelDetailData(updatedHotelData)
            // Update original data after successful save
            originalHotelDetailData.current = JSON.parse(JSON.stringify(updatedHotelData))
            isInitialLoad.current = false

            // Reset validation errors after successful save
            await GetHotelDetails(true)
            // Wait a bit for all state updates to complete
            await new Promise(resolve => setTimeout(resolve, 100))
            setShowHotelDetailsValidationErrors(false)
            setRatingError(undefined)

            showSuccess("Hotel details saved successfully")
            return true
        } catch (error) {
            showError("Failed to save hotel details")
            return false
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <HotelDayWisePricingProvider>
            <div className="hotel-detail-container">
                {isLoading && <Loader message="Loading..." overlay={true} />}
                {/* Header */}
                <div className="hotel-detail-header">
                    <h1 className="hotel-title"> <span style={{ cursor: "pointer" }} onClick={() => navigate("/hotels")}> {"<-"} </span> {toTitleCase(hotelDetailData?.hotel_name)}</h1>
                </div>

                {/* Tabs */}
                <div className="hotel-detail-tabs">
                    <button className={`tab ${activeTab === "rates-overview" ? "active" : ""}`} onClick={() => handleTabChange("rates-overview")}>
                        Rates Overview
                    </button>
                    <button className={`tab ${activeTab === "hotel-details" ? "active" : ""}`} onClick={() => handleTabChange("hotel-details")}>
                        Hotel Details
                    </button>
                    <button className={`tab ${activeTab === "room-categories" ? "active" : ""}`} onClick={() => handleTabChange("room-categories")}>
                        Room Categories
                    </button>
                    <button className={`tab ${activeTab === "rates" ? "active" : ""}`} onClick={() => handleTabChange("rates")}>
                        Rates
                    </button>
                </div>

                {/* Tab Content */}
                {activeTab === "rates-overview" && (
                    <div className="hotel-detail-content">
                        <RatesOverview
                            contracts={hotelDetailData?.contract ?? []}
                            hotelRoomTypes={hotelDetailData?.hotel_room_type ?? []}
                        />
                    </div>
                )}

                {activeTab === "hotel-details" && (
                    <>
                        <div className="hotel-detail-content">
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 12, md: 9 }}>
                                    {/* Row 1: Hotel Name, City, Star Rating, Address */}
                                    <Grid container spacing={2} marginBottom="20px">
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <InputText
                                                label="Hotel Name"
                                                id={HOTEL_NAME_INPUT_ID}
                                                value={isHotelNameEditing ? hotelNameDraft : (toTitleCase(hotelDetailData?.hotel_name) || "")}
                                                onChange={handleHotelNameChange}
                                                placeholder="Enter hotel name"
                                                disabled={!isHotelNameEditing || isHotelNameSaving}
                                                error={hotelNameError}
                                                rightIcon={
                                                    <IconsColor
                                                        iconName={isHotelNameEditing ? "checkIcon" : "editIcon"}
                                                        width="18px"
                                                        height="18px"
                                                        fillColor="none"
                                                        strokeColor="currentColor"
                                                    />
                                                }
                                                onRightIconClick={isHotelNameEditing ? handleHotelNameConfirmClick : handleHotelNameEditClick}
                                                rightIconAriaLabel={isHotelNameEditing ? "Save hotel name" : "Edit hotel name"}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <InputText label="City" value={toTitleCase(hotelDetailData?.city_name) || ""} onChange={() => { }} placeholder="Enter city" disabled={true} />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <div className="star-rating-field">
                                                <label className="field-label">Star Rating <span style={{ color: "red" }}>*</span></label>
                                                <div className="star-rating">
                                                    {[1, 2, 3, 4, 5].map((star) => {
                                                        const currentRating = hotelDetailData?.rating || 0
                                                        const isFull = star <= currentRating
                                                        const isHalf = star === Math.ceil(currentRating) && currentRating % 1 !== 0

                                                        return (
                                                            <button
                                                                key={star}
                                                                type="button"
                                                                className="star-button"
                                                                onClick={(e) => {
                                                                    e.preventDefault()
                                                                    e.stopPropagation()
                                                                    // Double click for full star
                                                                    const newRating = star
                                                                    // Only update if value has changed
                                                                    if (originalHotelDetailData.current?.rating !== newRating) {
                                                                        handleHotelDetailChange("rating", newRating)
                                                                        updateHotelDetailField({ rating: newRating })
                                                                    }
                                                                    // Clear error when rating is set
                                                                    if (newRating > 0) {
                                                                        setRatingError(undefined)
                                                                    }
                                                                }}
                                                                style={{ position: "relative", padding: 0 }}
                                                            >
                                                                {/* Background star (empty/stroke only) */}
                                                                <IconsColor iconName="star" width="24" height="24" fillColor="none" strokeColor="#FFC107" />
                                                                {/* Foreground star (filled) */}
                                                                {(isFull || isHalf) && (
                                                                    <div
                                                                        style={{
                                                                            position: "absolute",
                                                                            top: 0,
                                                                            left: 0,
                                                                            width: isHalf ? "50%" : "100%",
                                                                            overflow: "hidden",
                                                                        }}
                                                                    >
                                                                        <IconsColor iconName="star" width="24" height="24" fillColor="#FFC107" strokeColor="#FFC107" />
                                                                    </div>
                                                                )}
                                                            </button>
                                                        )
                                                    })}
                                                    {/* <span style={{ marginLeft: "10px", fontSize: "14px", color: "#666" }}>{hotelDetailData?.rating ? Number(hotelDetailData.rating) : "0"}</span> */}
                                                </div>
                                                {(ratingError || (showHotelDetailsValidationErrors && (!hotelDetailData?.rating || hotelDetailData.rating <= 0))) && (
                                                    <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                                                        {ratingError || "Star rating is required"}
                                                    </div>
                                                )}
                                            </div>
                                        </Grid>
                                    </Grid>

                                    {/* Row 2: Hotel Type, Contact Number, Google Place ID */}
                                    <Grid container spacing={2} marginBottom="20px">
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Select
                                                label="Hotel Type"
                                                value={String(hotelDetailData?.hotel_type_id || "")}
                                                onChange={handleHotelTypeChange}
                                                options={hotelGenericData?.hotel_type?.map((x: any) => ({ label: x.hotel_type_name, value: String(x.hotel_type_id) })) || []}
                                                placeholder="Type"
                                                required={true}
                                                error={showHotelDetailsValidationErrors && !hotelDetailData?.hotel_type_id ? "Required" : undefined}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <PhoneInputComponent
                                                label="Contact Number"
                                                value={hotelDetailData?.contact_number || ""}
                                                onChange={(value) => {
                                                    handleHotelDetailChange("contact_number", value)
                                                    setContactNumberError(undefined) // Clear error on change
                                                }}
                                                onBlur={handleContactNumberBlur}
                                                placeholder="Enter phone number"
                                                required
                                                defaultCountry="VN"
                                                error={contactNumberError || (showHotelDetailsValidationErrors && (!hotelDetailData?.contact_number || hotelDetailData.contact_number.replace(/\s/g, '').length === 0) ? "Required" : undefined)}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                            <InputText
                                                label="Website"
                                                value={hotelDetailData?.hotel_link || ""}
                                                onChange={handleHotelLinkChange}
                                                onBlur={handleHotelLinkBlur}
                                                placeholder="https://www.example.com"
                                                type="url"
                                                error={hotelLinkError}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 12, md: 3 }}>
                                    <TextArea
                                        label="Address"
                                        value={toTitleCase(hotelDetailData?.address) || ""}
                                        onChange={(e) => {
                                            handleHotelDetailChange("address", toTitleCase(e.target.value))
                                            setAddressError(undefined) // Clear error on change
                                        }}
                                        onBlur={handleAddressBlur}
                                        placeholder="Enter Address"
                                        height={"150px"}
                                        required={true}
                                        error={addressError || (showHotelDetailsValidationErrors && (!hotelDetailData?.address || hotelDetailData.address.trim().length === 0) ? "Required" : undefined)}
                                    />
                                </Grid>
                                {/* Row 3: Latitude, Longitude, Status */}
                                <Grid container spacing={2} marginBottom="20px" size={{ xs: 12, sm: 12, md: 12 }}>
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <InputText
                                            label="Google Place ID"
                                            value={hotelDetailData?.google_place_id || ""}
                                            onChange={(e) => handleHotelDetailChange("google_place_id", e.target.value)}
                                            onBlur={handleGooglePlaceIdBlur}
                                            placeholder="Enter Google Place ID"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <InputText
                                            label="Latitude"
                                            value={hotelDetailData?.latitude || ""}
                                            onChange={handleLatitudeChange}
                                            onBlur={handleLatitudeBlur}
                                            placeholder="Enter Latitude (-90 to 90)"
                                            type="text"
                                            error={latitudeError}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <InputText
                                            label="Longitude"
                                            value={hotelDetailData?.longitude || ""}
                                            onChange={handleLongitudeChange}
                                            onBlur={handleLongitudeBlur}
                                            placeholder="Enter Longitude (-180 to 180)"
                                            type="text"
                                            error={longitudeError}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        <div className="status-field">
                                            <label className="field-label">Status</label>
                                            <div className="status-toggle">
                                                <Toggle
                                                    checked={hotelDetailData?.flag_active || false}
                                                    onChange={(checked) => {
                                                        // Only update if value has changed
                                                        if (originalHotelDetailData.current?.flag_active !== checked) {
                                                            handleHotelDetailChange("flag_active", checked)
                                                            updateHotelDetailField({ flag_active: checked })
                                                        }
                                                    }}
                                                    leftLabel="Inactive"
                                                    rightLabel="Active"
                                                />
                                            </div>
                                        </div>
                                    </Grid>
                                </Grid>
                            </Grid>
                            {/* Hotel Amenities */}
                            <div className="amenities-section">
                                <h3 className="amenities-title">Hotel Amenities</h3>
                                <Grid container spacing={2}>
                                    {hotelDetailData?.hotel_amenities?.map((amenity: HotelAmenityModel) => (
                                        <Grid key={amenity.hotel_amenities_type_id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                                            <Checkbox label={amenity.hotel_amenities_type_name} checked={amenity.flag_configured} onChange={(checked) => handleAmenityChange(amenity.hotel_amenities_type_id, checked)} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </div>
                        </div>

                        <div style={{ marginTop: "30px" }}>
                            <Grid container spacing={2} style={{ display: "flex", alignItems: "center" }}>
                                <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                                    <h1 className="hotel-title">Contracts</h1>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 12, md: 6 }} style={{ display: "flex", justifyContent: "flex-end", gap: "30px" }}>
                                    <button type="button" className="addBtn" onClick={handleAddContract}>
                                        + Add Contract
                                    </button>
                                    <Button type="contained" label="Save" onClick={() => handleSaveHotelDetails()} />
                                </Grid>
                            </Grid>
                        </div>

                        <div>
                            {hotelDetailData?.contract && hotelDetailData?.contract?.length > 0 ? (
                                hotelDetailData?.contract.map((x: HotelContractModel, index: number) => {
                                    const canDeleteContract = (hotelDetailData?.contract?.length ?? 0) > 1
                                    return (
                                        <div key={x.contract_id || index} className="hotel-detail-content" style={{ marginTop: "30px" }}>
                                            {canDeleteContract && (
                                                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
                                                    <button
                                                        type="button"
                                                        className="deleteContractBtn"
                                                        onClick={() => handleDeleteContractClick(x, index)}
                                                    >
                                                        Delete Contract
                                                    </button>
                                                </div>
                                            )}
                                            <HotelContract
                                                contract={[x]}
                                                dayWisePricingScopeId={String(x.contract_id ?? `local-${index}`)}
                                                onChange={(updatedContract) => handleContractChange(index, updatedContract)}
                                                validateContractDates={(startDate, endDate) => getContractDateErrors(index, startDate, endDate)}
                                                onErrorStatusChange={(errors) => handleContractErrorsChange(index, errors)}
                                                showValidationErrors={showHotelDetailsValidationErrors}
                                            />
                                        </div>
                                    )
                                })
                            ) : (
                                <div className="hotel-detail-content" style={{ marginTop: "20px" }}>
                                    <HotelContract
                                        contract={[]}
                                        dayWisePricingScopeId="local-0"
                                        onChange={(updatedContract) => {
                                            // Handle new contract (when contract array is empty)
                                            setHotelDetailData((prevData) => {
                                                if (!prevData) return prevData
                                                return {
                                                    ...prevData,
                                                    contract: [...prevData.contract, updatedContract],
                                                }
                                            })
                                        }}
                                        validateContractDates={(startDate, endDate) => getContractDateErrors(0, startDate, endDate)}
                                        onErrorStatusChange={(errors) => handleContractErrorsChange(0, errors)}
                                        showValidationErrors={showHotelDetailsValidationErrors}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}

                {activeTab === "room-categories" && (
                    <RoomCategories
                        hotelId={hotelDetailData?.hotel_id ?? ""}
                        roomTypes={hotelDetailData?.hotel_room_type ?? []}
                        hotelDetailData={hotelDetailData}
                        onChange={(next) => {
                            setHotelDetailData((prev) => {
                                if (!prev) return prev
                                return { ...prev, hotel_room_type: next }
                            })
                        }}
                        onValidationError={(hasErrors) => setRoomCategoriesHasErrors(hasErrors)}
                        onSaveRef={(saveFunc) => {
                            roomCategoriesSaveRef.current = saveFunc
                        }}
                        onSaveSuccess={async (meta) => {
                            const fresh = await GetHotelDetails(true)
                            await new Promise((resolve) => setTimeout(resolve, 100))
                            if (!meta?.anyDifferentSurchargeSupport || !fresh) {
                                return fresh
                            }
                            const merged: HotelDetailModel = {
                                ...fresh,
                                contract: contractsWithDayWiseSurchargesCleared(fresh.contract),
                            }
                            setHotelDetailData(merged)
                            if (!isInitialLoad.current) {
                                originalHotelDetailData.current = JSON.parse(JSON.stringify(merged))
                            }
                            return merged
                        }}
                        showValidationErrors={showRoomCategoriesValidationErrors}
                    />
                )}

                {activeTab === "rates" && (
                    <HotelRates
                        hotelId={hotelDetailData?.hotel_id ?? ""}
                        contracts={hotelDetailData?.contract ?? []}
                        hotelRoomTypes={hotelDetailData?.hotel_room_type ?? []}
                        dayWisePricingScopeId={ratesDayWisePricingScopeId}
                        selectedContractId={selectedRatesContractId}
                        onContractChange={(contractId) => setSelectedRatesContractId(contractId)}
                        onChange={handleRatesPricingChange}
                        onFullChange={handleRatesFullChange}
                        onValidationError={(hasErrors) => setHotelRatesHasErrors(hasErrors)}
                        onSaveRef={(saveFunc) => {
                            hotelRatesSaveRef.current = saveFunc
                        }}
                        onSaveSuccess={async () => {
                            // Refresh hotel details to get updated data from server
                            // GetHotelDetails will update originalHotelDetailData.current automatically
                            await GetHotelDetails(true)
                            // Wait a bit for all state updates to complete
                            await new Promise(resolve => setTimeout(resolve, 100))
                        }}
                        showValidationErrors={showHotelRatesValidationErrors}
                    />
                )}

                {/* Error Popup */}
                <Popup
                    isOpen={showErrorPopup}
                    onClose={handleCancelTabChange}
                    title="Validation Errors"
                    size="medium"
                >
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
                        <IconsColor iconName="warningSign" width="80px" height="80px" fillColor="none" strokeColor="#FF0000" />
                    </div>
                    <p style={{ textAlign: "center" }}>Please fix all validation errors before switching tabs.</p>
                    {activeTab === "hotel-details" && (
                        <HotelValidationErrorsList hotelErrors={getHotelFieldErrors()} contractGroups={contractErrorGroups} />
                    )}
                    <div style={{ display: "flex", gap: "10px", marginTop: "20px", justifyContent: "center" }}>
                        <Button type="outlined" style={{ width: "100%" }} label="Discard Changes" onClick={handleDiscardChanges} />
                        <Button type="contained" style={{ width: "100%" }} label="Fix Errors" onClick={handleFixErrors} />
                    </div>
                </Popup>

                {/* Hotel Details Validation Errors Popup (on save) */}
                <Popup
                    isOpen={showValidationErrorsPopup}
                    onClose={() => setShowValidationErrorsPopup(false)}
                    title="Validation Errors"
                    size="medium"
                >
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
                        <IconsColor iconName="warningSign" width="80px" height="80px" fillColor="none" strokeColor="#FF0000" />
                    </div>
                    <p style={{ textAlign: "center" }}>Please fix the following errors before saving.</p>
                    <HotelValidationErrorsList hotelErrors={getHotelFieldErrors()} contractGroups={contractErrorGroups} />
                    <div style={{ display: "flex", gap: "10px", marginTop: "20px", justifyContent: "center" }}>
                        <Button type="contained" style={{ width: "100%" }} label="Fix Errors" onClick={handleFixErrors} />
                    </div>
                </Popup>

                {/* Unsaved Changes Popup */}
                <Popup
                    isOpen={showUnsavedChangesPopup}
                    onClose={handleCancelTabChange}
                    title="Unsaved Changes"
                    size="medium"
                    closeOnOverlayClick={false}
                >
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
                        <IconsColor iconName="warningSign" width="80px" height="80px" fillColor="none" strokeColor="#FF0000" />
                    </div>
                    <p style={{ textAlign: "center" }}>You have unsaved changes. What would you like to do?</p>
                    <div style={{ display: "flex", gap: "10px", marginTop: "20px", justifyContent: "center" }}>
                        <Button type="outlined" style={{ width: "100%" }} label="Cancel" onClick={handleCancelTabChange} />
                        <Button type="outlined" style={{ width: "100%" }} label="Discard Changes" onClick={handleDiscardChanges} />
                        <Button type="contained" style={{ width: "100%" }} label="Save and Continue" onClick={handleSaveAndContinue} />
                    </div>
                </Popup>

                {/* Delete Contract Confirmation Popup */}
                <Popup
                    isOpen={showDeleteContractPopup}
                    onClose={handleCancelDeleteContract}
                    title="Delete Contract"
                    primaryButton={{
                        label: "Confirm",
                        onClick: handleConfirmDeleteContract,
                    }}
                    secondaryButton={{
                        label: "Cancel",
                        onClick: handleCancelDeleteContract,
                    }}
                    heightFiexd={true}
                >
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "20px" }}>
                        <IconsColor iconName="warningSign" width="80px" height="80px" fillColor="none" strokeColor="#FF0000" />
                        {contractToDelete && (
                            <p style={{ textAlign: "center", marginTop: "16px" }}>
                                Are you sure you want to delete <strong>this contract</strong>?
                            </p>
                        )}
                    </div>
                </Popup>
            </div>
        </HotelDayWisePricingProvider>
    )
}

export default HotelDetail
